import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Package, Eye } from "lucide-react";

const empty = () => ({ company_name: "", vendor_name: "", contact_name: "", email: "", phone: "", services: [{ name: "", charge: 0, timeline: "", remarks: "" }], notes: "" });

export default function Vendors() {
  const { user } = useAuth();
  const canManage = ["SuperAdmin", "TeamLeader"].includes(user.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty());
  const [search, setSearch] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/vendors"); setItems(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty()); setEditingId(null); setOpen(true); };
  const openEdit = (v) => {
    setForm({
      company_name: v.company_name, vendor_name: v.vendor_name || "", contact_name: v.contact_name || "",
      email: v.email || "", phone: v.phone || "", notes: v.notes || "",
      services: (v.services && v.services.length) ? v.services.map((s) => ({ ...s })) : [{ name: "", charge: 0, timeline: "", remarks: "" }],
    });
    setEditingId(v.id); setOpen(true);
  };
  const addService = () => setForm((f) => ({ ...f, services: [...f.services, { name: "", charge: 0, timeline: "", remarks: "" }] }));
  const updService = (i, patch) => setForm((f) => ({ ...f, services: f.services.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const removeService = (i) => setForm((f) => ({ ...f, services: f.services.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      services: form.services.filter((s) => s.name).map((s) => ({ ...s, charge: Number(s.charge) || 0 })),
    };
    try {
      if (editingId) await api.patch(`/vendors/${editingId}`, payload);
      else await api.post("/vendors", payload);
      setOpen(false); load();
      toast.success(editingId ? "Vendor updated" : "Vendor created");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete vendor "${v.company_name}"? Linked projects will be set to Non-Type.`)) return;
    try { await api.delete(`/vendors/${v.id}`); load(); toast.success("Vendor deleted"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = items.filter((v) => !search ||
    (v.company_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (v.vendor_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (v.email || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <PageHeader title="Vendors"
        description="Vendor master — companies that your workspace works with. Project Type on each project links to a vendor."
        actions={canManage && (
          <button data-testid="new-vendor-btn" onClick={openNew} className="btn-primary inline-flex items-center gap-2"><Plus size={14} /> Add New Vendor</button>
        )} />
      <div className="p-8 space-y-4">
        <Input data-testid="vendor-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by company, vendor, or email…" className="h-10 max-w-md rounded-lg border-black/15" />
        <div className="bg-white border border-black/10 rounded-2xl overflow-hidden">
          <table className="fms-table w-full" data-testid="vendors-table">
            <thead><tr><th>Company</th><th>Vendor</th><th>Email</th><th>Phone</th><th>Contact</th><th>Services</th><th>Projects Linked</th><th>Project Type</th><th className="text-right">Actions</th></tr></thead>
            <tbody>
              {filtered.map((v) => (
                <tr key={v.id} data-testid={`vendor-row-${v.id}`}>
                  <td className="font-medium"><Link to={`/vendors/${v.id}`} data-testid={`vendor-link-${v.id}`} className="underline underline-offset-4">{v.company_name}</Link></td>
                  <td className="text-sm">{v.vendor_name || "—"}</td>
                  <td className="text-sm text-neutral-600">{v.email || "—"}</td>
                  <td className="font-mono-tight text-xs">{v.phone || "—"}</td>
                  <td className="text-sm">{v.contact_name || "—"}</td>
                  <td className="text-xs text-ink/70">{(v.services || []).map((s) => s.name).slice(0, 3).join(", ") || "—"}</td>
                  <td className="font-mono-tight" data-testid={`vendor-projects-count-${v.id}`}>{v.projects_linked_count ?? 0}</td>
                  <td className="text-xs font-mono-tight text-royal-800">{v.company_name}</td>
                  <td className="text-right whitespace-nowrap space-x-3">
                    <Link data-testid={`view-vendor-${v.id}`} to={`/vendors/${v.id}`} className="text-xs underline underline-offset-4 inline-flex items-center gap-1"><Eye size={12} /> View</Link>
                    {canManage && <>
                      <button data-testid={`edit-vendor-${v.id}`} onClick={() => openEdit(v)} className="text-xs underline underline-offset-4"><Pencil size={12} className="inline mr-1" />Edit</button>
                      <button data-testid={`delete-vendor-${v.id}`} onClick={() => remove(v)} className="text-xs underline underline-offset-4 text-red-600"><Trash2 size={12} className="inline mr-1" />Delete</button>
                    </>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={9} className="text-center text-neutral-500 py-8">No vendors yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-[1.25rem] max-w-2xl max-h-[85vh] overflow-auto" data-testid="vendor-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit vendor" : "Add new vendor"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-caps block mb-1">Company Name *</label><Input data-testid="vendor-company" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="rounded-lg border-black/15" /></div>
              <div><label className="label-caps block mb-1">Vendor Name</label><Input data-testid="vendor-name" value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} className="rounded-lg border-black/15" /></div>
              <div><label className="label-caps block mb-1">Contact Name</label><Input data-testid="vendor-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-lg border-black/15" /></div>
              <div><label className="label-caps block mb-1">Email</label><Input data-testid="vendor-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg border-black/15" /></div>
              <div><label className="label-caps block mb-1">Phone</label><Input data-testid="vendor-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-lg border-black/15" /></div>
            </div>
            <div>
              <div className="flex items-baseline justify-between mb-2"><label className="label-caps">Services</label>
                <button type="button" data-testid="add-service-btn" onClick={addService} className="text-xs underline underline-offset-4">+ Add service</button>
              </div>
              <div className="space-y-2">
                {form.services.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`service-row-${i}`}>
                    <Input data-testid={`service-name-${i}`} className="col-span-4 rounded-lg border-black/15 h-9" placeholder="Service name" value={s.name} onChange={(e) => updService(i, { name: e.target.value })} />
                    <Input data-testid={`service-charge-${i}`} type="number" className="col-span-2 rounded-lg border-black/15 h-9" placeholder="Charge" value={s.charge} onChange={(e) => updService(i, { charge: e.target.value })} />
                    <Input data-testid={`service-timeline-${i}`} className="col-span-2 rounded-lg border-black/15 h-9" placeholder="Timeline" value={s.timeline} onChange={(e) => updService(i, { timeline: e.target.value })} />
                    <Input data-testid={`service-remarks-${i}`} className="col-span-3 rounded-lg border-black/15 h-9" placeholder="Remarks" value={s.remarks} onChange={(e) => updService(i, { remarks: e.target.value })} />
                    {form.services.length > 1 && <button type="button" onClick={() => removeService(i)} className="col-span-1 text-red-600"><Trash2 size={14} /></button>}
                  </div>
                ))}
              </div>
            </div>
            <div><label className="label-caps block mb-1">Notes</label><Textarea data-testid="vendor-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-lg border-black/15" /></div>
            <DialogFooter><button data-testid="vendor-submit" type="submit" className="btn-primary">{editingId ? "Save changes" : "Create vendor"}</button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
